/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

import api, { ApiResponse } from './api';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  companyId: string;
}

export interface AuthResponse {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    roles: any[];
  };
  accessToken: string;
  refreshToken: string;
}

export const authService = {
  /**
   * Login user
   */
  async login(credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> {
    const response = await api.post('/api/v1/auth/login', credentials);
    return response.data;
  },

  /**
   * Register new user
   */
  async register(data: RegisterData): Promise<ApiResponse> {
    const response = await api.post('/api/v1/auth/register', data);
    return response.data;
  },

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    await api.post('/api/v1/auth/logout');
  },

  /**
   * Forgot password
   */
  async forgotPassword(email: string): Promise<ApiResponse> {
    const response = await api.post('/api/v1/auth/forgot-password', { email });
    return response.data;
  },

  /**
   * Reset password
   */
  async resetPassword(token: string, password: string): Promise<ApiResponse> {
    const response = await api.post('/api/v1/auth/reset-password', { token, password });
    return response.data;
  },

  /**
   * Get current user profile
   */
  async getProfile(): Promise<ApiResponse> {
    const response = await api.get('/api/v1/users/profile');
    return response.data;
  },

  /**
   * Update user profile
   */
  async updateProfile(data: any): Promise<ApiResponse> {
    const response = await api.put('/api/v1/users/profile', data);
    return response.data;
  },

  /**
   * Change password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse> {
    const response = await api.put('/api/v1/users/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },
};

export default authService;

